import { useTranslation } from "react-i18next";
import { Search, X } from "lucide-react";
import { Input } from "@/shared/components/ui/input";
import { Button } from "@/shared/components/ui/button";
import { VENUE_STATUS_FILTERS, VENUE_TYPE_FILTERS } from "./constants";
import type { VenueStatusFilter, VenueTypeFilter } from "./types";

interface VenuesFiltersProps {
  readonly query: string;
  readonly status: VenueStatusFilter;
  readonly type: VenueTypeFilter;
  readonly onQueryChange: (value: string) => void;
  readonly onStatusChange: (value: VenueStatusFilter) => void;
  readonly onTypeChange: (value: VenueTypeFilter) => void;
  readonly onReset: () => void;
}

const SELECT_CLASS =
  "h-10 rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2";

export function VenuesFilters({
  query,
  status,
  type,
  onQueryChange,
  onStatusChange,
  onTypeChange,
  onReset,
}: VenuesFiltersProps) {
  const { t } = useTranslation();
  const hasFilters = query.length > 0 || status !== "all" || type !== "all";

  return (
    <div className="flex flex-wrap items-end gap-3">
      <div className="min-w-[260px] flex-1 space-y-1.5">
        <label htmlFor="venues-search" className="font-sans text-sm font-medium text-foreground">
          {t("venuesDirectory.filters.search")}
        </label>
        <div className="relative">
          <Search
            className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
            aria-hidden="true"
          />
          <Input
            id="venues-search"
            data-testid="venues-search-input"
            type="search"
            value={query}
            onChange={(e) => onQueryChange(e.target.value)}
            placeholder={t("venuesDirectory.filters.searchPlaceholder")}
            className="pl-9"
            autoComplete="off"
          />
        </div>
      </div>

      <div className="space-y-1.5">
        <label
          htmlFor="venues-status-filter"
          className="font-sans text-sm font-medium text-foreground"
        >
          {t("venuesDirectory.filters.status")}
        </label>
        <select
          id="venues-status-filter"
          data-testid="filter-status"
          value={status}
          onChange={(e) => onStatusChange(e.target.value as VenueStatusFilter)}
          className={SELECT_CLASS}
        >
          {VENUE_STATUS_FILTERS.map((s) => (
            <option key={s} value={s}>
              {s === "all"
                ? t("venuesDirectory.filters.statusAll")
                : t(`venuesDirectory.statusLabel.${s}`)}
            </option>
          ))}
        </select>
      </div>

      <div className="space-y-1.5">
        <label
          htmlFor="venues-type-filter"
          className="font-sans text-sm font-medium text-foreground"
        >
          {t("venuesDirectory.filters.type")}
        </label>
        <select
          id="venues-type-filter"
          data-testid="filter-type"
          value={type}
          onChange={(e) => onTypeChange(e.target.value as VenueTypeFilter)}
          className={SELECT_CLASS}
        >
          {VENUE_TYPE_FILTERS.map((ty) => (
            <option key={ty} value={ty}>
              {ty === "all"
                ? t("venuesDirectory.filters.typeAll")
                : t(`venuesDirectory.typeLabel.${ty}`)}
            </option>
          ))}
        </select>
      </div>

      {hasFilters && (
        <Button
          data-testid="venues-filters-reset"
          variant="ghost"
          onClick={onReset}
          className="h-10"
        >
          <X className="mr-1 h-4 w-4" />
          {t("venuesDirectory.filters.reset")}
        </Button>
      )}
    </div>
  );
}
